import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserAchievements } from '@/features/profile/hooks/useUserAchievements';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/features/profile/hooks/useUserProfile';
import { supabase } from '@/integrations/supabase/client';

// Import achievement icons
import trophyIcon from '@/assets/images/achievements/trophy.png';
import goalIcon from '@/assets/images/achievements/goal.png';
import stripePromotionIcon from '@/assets/images/achievements/stripe_promotion.png';
import beltIcon from '@/assets/images/achievements/belt.png';
import AwardIcon from '@/assets/icons/award.svg';

interface Achievement {
  id: string;
  type: 'training_goal' | 'belt_promotion' | 'stripe_promotion' | 'training_milestone' | 'ranked_up';
  title: string;
  description: string;
  created_at: string;
  metadata: {
    month?: string;
    trainings_count?: number;
    belt_color?: string;
    school?: string;
    ordinal_stripe?: string;
    technique_category?: string;
  };
}

const UserAchievements: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { data: achievements, isLoading, error } = useUserAchievements(userId || '');
  const { t, currentLanguage } = useI18n();
  const { user } = useAuth();
  const { profile: myProfile } = useUserProfile();
  const [friendName, setFriendName] = useState<string>('');

  // Fetch user's name (friend or own) when userId changes
  useEffect(() => {
    const fetchUserName = async () => {
      if (!userId) return;
      
      // If viewing own profile, use myProfile
      if (userId === user?.id) {
        if (myProfile?.full_name) {
          setFriendName(myProfile.full_name);
        }
        return;
      }
      
      // If viewing friend's profile, fetch their name
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', userId)
          .single();
        
        if (data?.full_name) {
          setFriendName(data.full_name);
        }
      } catch (error) {
        console.error('Error fetching friend name:', error);
      }
    };

    fetchUserName();
  }, [userId, user?.id, myProfile?.full_name]);

  // Format date correctly handling timezone issues
  // Always extract date components and create Date object locally to avoid UTC conversion
  const formatDate = (dateString: string) => {
    // Extract date components from string (handles YYYY-MM-DD, ISO with time, etc.)
    let year: number, month: number, day: number;
    
    // Try to extract YYYY-MM-DD pattern from the string
    const dateMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      year = parseInt(dateMatch[1], 10);
      month = parseInt(dateMatch[2], 10);
      day = parseInt(dateMatch[3], 10);
    } else {
      // Fallback: try to parse as Date and extract components
      const tempDate = new Date(dateString);
      if (isNaN(tempDate.getTime())) {
        // If still invalid, return original string
        return dateString;
      }
      year = tempDate.getFullYear();
      month = tempDate.getMonth() + 1;
      day = tempDate.getDate();
    }
    
    // Create Date object using local timezone (no UTC conversion)
    const date = new Date(year, month - 1, day);
    
    // Format as dd/mm/yyyy for Portuguese, mm/dd/yyyy for English
    if (currentLanguage === 'pt') {
      // Use the extracted day/month/year directly to avoid any timezone issues
      const dayStr = String(day).padStart(2, '0');
      const monthStr = String(month).padStart(2, '0');
      const yearStr = String(year);
      return `${dayStr}/${monthStr}/${yearStr}`;
    } else {
      const locale = 'en-US';
      return date.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  // Get date label based on achievement type
  const getDateLabel = (type: string): string => {
    switch (type) {
      case 'belt_promotion':
      case 'stripe_promotion':
        return t('achievements.earned_on');
      case 'training_milestone':
        return t('achievements.achieved_on');
      case 'training_goal':
        return t('achievements.completed_on');
      default:
        return t('achievements.achieved_on');
    }
  };

  const getAchievementIcon = (type: string) => {
    switch (type) {
      case 'training_goal':
        return goalIcon;
      case 'belt_promotion':
        return beltIcon;
      case 'stripe_promotion':
        return stripePromotionIcon;
      case 'training_milestone':
        return trophyIcon;
      case 'ranked_up':
        return trophyIcon;

      default:
        return trophyIcon;
    }
  };

  // Function to translate achievement title and description from database values
  const translateAchievement = (achievement: Achievement) => {
    const { type, title, description, metadata } = achievement;
    
    // Get user name for achievements - use profile name as fallback
    const getUserName = (): string => {
      // First try friendName (set by useEffect)
      if (friendName) {
        return friendName;
      }
      // If viewing own profile, use myProfile
      if (userId === user?.id && myProfile?.full_name) {
        return myProfile.full_name;
      }
      // Fallback to "Você" / "You"
      return currentLanguage === 'pt' ? 'Você' : 'You';
    };
    
    // Helper function to translate belt colors from English to Portuguese
    const translateBeltColor = (color: string): string => {
      const colorMap: { [key: string]: string } = {
        'white': 'branca',
        'blue': 'azul',
        'purple': 'roxa',
        'brown': 'marrom',
        'black': 'preta'
      };
      return colorMap[color.toLowerCase()] || color;
    };
    
    // Dynamic title translation based on content analysis
    let translatedTitle = title;
    
    // Handle training_milestone achievements (these come from database with Portuguese titles)
    if (type === 'training_milestone') {
      // Map Portuguese titles to translation keys
      const milestoneTitleMap: Record<string, string> = {
        'O Primeiro Passo': 'home.achievements.milestones.1.title',
        'Em Progresso': 'home.achievements.milestones.in_progress.title',
        'Dez e Contando': 'home.achievements.milestones.10.title',
        'Quarto de Século': 'home.achievements.milestones.25.title',
        'Meio Século de Rolagens': 'home.achievements.milestones.50.title',
        'A Marca do Século': 'home.achievements.milestones.100.title',
        'O Duplo Século': 'home.achievements.milestones.200.title',
        'Meio Milhar de Rolagens': 'home.achievements.milestones.500.title',
        'O Guerreiro das Mil Horas': 'home.achievements.milestones.1000.title',
      };
      
      // Also map English titles (in case they exist)
      const milestoneTitleMapEn: Record<string, string> = {
        'The First Step': 'home.achievements.milestones.1.title',
        'In Progress': 'home.achievements.milestones.in_progress.title',
        'Ten and Counting': 'home.achievements.milestones.10.title',
        'Quarter Century': 'home.achievements.milestones.25.title',
        'Half Century of Rolls': 'home.achievements.milestones.50.title',
        'The Century Mark': 'home.achievements.milestones.100.title',
        'The Double Century': 'home.achievements.milestones.200.title',
        'Half a Thousand Rolls': 'home.achievements.milestones.500.title',
        'The Warrior of a Thousand Hours': 'home.achievements.milestones.1000.title',
      };
      
      const titleKey = milestoneTitleMap[title] || milestoneTitleMapEn[title];
      if (titleKey) {
        translatedTitle = t(titleKey);
      }
    } else {
      // Analyze title content and translate dynamically for other types
      const titlePatterns = [
        // Training goal patterns
        { pattern: /monthly/i, key: 'achievements.titles.training_goal.monthly_training' },
        { pattern: /technique/i, key: 'achievements.titles.training_goal.technique_mastery' },
        { pattern: /consistency/i, key: 'achievements.titles.training_goal.consistency_king' },
        { pattern: /endurance/i, key: 'achievements.titles.training_goal.endurance_champion' },
        { pattern: /flexibility/i, key: 'achievements.titles.training_goal.flexibility_guru' },
        { pattern: /strength/i, key: 'achievements.titles.training_goal.strength_warrior' },
        { pattern: /speed/i, key: 'achievements.titles.training_goal.speed_demon' },
        { pattern: /precision/i, key: 'achievements.titles.training_goal.precision_artist' },
        
        // Belt promotion patterns
        { pattern: /white.*blue/i, key: 'achievements.titles.belt_promotion.white_to_blue' },
        { pattern: /blue.*purple/i, key: 'achievements.titles.belt_promotion.blue_to_purple' },
        { pattern: /purple.*brown/i, key: 'achievements.titles.belt_promotion.purple_to_brown' },
        { pattern: /brown.*black/i, key: 'achievements.titles.belt_promotion.brown_to_black' },
        { pattern: /black.*belt/i, key: 'achievements.titles.belt_promotion.black_belt' },
        
        // Stripe promotion patterns
        { pattern: /first.*stripe/i, key: 'achievements.titles.stripe_promotion.first_stripe' },
        { pattern: /second.*stripe/i, key: 'achievements.titles.stripe_promotion.second_stripe' },
        { pattern: /third.*stripe/i, key: 'achievements.titles.stripe_promotion.third_stripe' },
        { pattern: /fourth.*stripe/i, key: 'achievements.titles.stripe_promotion.fourth_stripe' },
        
        
        // Exact title matches from database
        { pattern: /^stripe promotion!$/i, key: 'achievements.titles.stripe_promotion.general' },
        { pattern: /^belt promotion!$/i, key: 'achievements.titles.belt_promotion.general' },
      ];
      
      // Find matching pattern and translate
      for (const { pattern, key } of titlePatterns) {
        if (pattern.test(title)) {
          translatedTitle = t(key);
          break;
        }
      }
    }
    
    // Dynamic description translation based on type and content
    let translatedDescription = description;
    
    if (type === 'training_milestone') {
      // Map Portuguese descriptions to translation keys
      const milestoneDescriptionMap: Record<string, string> = {
        'No Tatame sOSSiety!': 'home.achievements.milestones.1.description',
        'Continue assim!': 'home.achievements.milestones.in_progress.description',
        'Dedicação de Dois Dígitos': 'home.achievements.milestones.10.description',
        'Vinte e Cinco Fortes': 'home.achievements.milestones.25.description',
        'Cinquenta Vezes Mais Forte': 'home.achievements.milestones.50.description',
        '100 Rolagens Fortes': 'home.achievements.milestones.100.description',
        'Um Veterano do Tatame': 'home.achievements.milestones.200.description',
        'Caminho para a Maestria': 'home.achievements.milestones.500.description',
        'Legado de Disciplina': 'home.achievements.milestones.1000.description',
      };
      
      // Also map English descriptions (in case they exist)
      const milestoneDescriptionMapEn: Record<string, string> = {
        'On the sOSSiety Mat!': 'home.achievements.milestones.1.description',
        'Keep it up!': 'home.achievements.milestones.in_progress.description',
        'Two-Digit Dedication': 'home.achievements.milestones.10.description',
        'Twenty-Five Strong': 'home.achievements.milestones.25.description',
        'Fifty Times Stronger': 'home.achievements.milestones.50.description',
        '100 Strong Rolls': 'home.achievements.milestones.100.description',
        'A Mat Veteran': 'home.achievements.milestones.200.description',
        'Path to Mastery': 'home.achievements.milestones.500.description',
        'Legacy of Discipline': 'home.achievements.milestones.1000.description',
      };
      
      const descriptionKey = milestoneDescriptionMap[description] || milestoneDescriptionMapEn[description];
      if (descriptionKey) {
        translatedDescription = t(descriptionKey);
      }
    } else if (type === 'training_goal') {
      // Extract numbers and values from description
      const numbers = description.match(/\d+/g);
      const count = numbers?.[0] || '15';
      
      if (description.toLowerCase().includes('trainings') || description.toLowerCase().includes('count')) {
        translatedDescription = t('achievements.descriptions.training_goal.monthly_training', { count });
      } else if (description.toLowerCase().includes('technique') || description.toLowerCase().includes('mastered')) {
        const technique = metadata?.technique_category || 'técnica';
        translatedDescription = t('achievements.descriptions.training_goal.technique_mastery', { technique });
      } else if (description.toLowerCase().includes('consecutive') || description.toLowerCase().includes('days')) {
        const days = numbers?.[0] || '30';
        translatedDescription = t('achievements.descriptions.training_goal.consistency_king', { days });
      } else if (description.toLowerCase().includes('endurance')) {
        translatedDescription = t('achievements.descriptions.training_goal.endurance_champion', { duration: '2 horas' });
      } else if (description.toLowerCase().includes('flexibility')) {
        translatedDescription = t('achievements.descriptions.training_goal.flexibility_guru');
      } else if (description.toLowerCase().includes('strength')) {
        const percentage = numbers?.[0] || '25';
        translatedDescription = t('achievements.descriptions.training_goal.strength_warrior', { percentage });
      } else if (description.toLowerCase().includes('speed')) {
        translatedDescription = t('achievements.descriptions.training_goal.speed_demon', { time: '0.5s' });
      } else if (description.toLowerCase().includes('precision')) {
        const accuracy = numbers?.[0] || '95';
        translatedDescription = t('achievements.descriptions.training_goal.precision_artist', { accuracy });
      }
    } else if (type === 'belt_promotion') {
      // Get user name - always use profile name as primary source for reliability
      const userName = getUserName();
      
      if (description.toLowerCase().includes('white to blue')) {
        translatedDescription = t('achievements.descriptions.belt_promotion.white_to_blue');
      } else if (description.toLowerCase().includes('blue to purple')) {
        translatedDescription = t('achievements.descriptions.belt_promotion.blue_to_purple');
      } else if (description.toLowerCase().includes('purple to brown')) {
        translatedDescription = t('achievements.descriptions.belt_promotion.purple_to_brown');
      } else if (description.toLowerCase().includes('brown to black')) {
        translatedDescription = t('achievements.descriptions.belt_promotion.brown_to_black');
      } else {
        // For all other belt promotions (including "conquered" and "black belt")
        // Extract belt color from description or metadata
        let beltColor: string | null = null;
        
        // Try to extract from description using regex
        let beltMatch = description.match(/(.+) conquered the (\w+) belt(?: in the \(.+\))?[!.]?/i);
        if (!beltMatch) {
          beltMatch = description.match(/(.+) conquered the (\w+) belt/i);
        }
        if (!beltMatch) {
          beltMatch = description.match(/conquered the (\w+) belt/i);
        }
        
        if (beltMatch && beltMatch[2]) {
          beltColor = beltMatch[2].toLowerCase();
        } else if (metadata?.belt_color) {
          // Fallback to metadata if regex didn't work
          beltColor = metadata.belt_color.toLowerCase();
        } else {
          // Last resort: try to detect from description keywords
          if (description.toLowerCase().includes('black')) beltColor = 'black';
          else if (description.toLowerCase().includes('brown')) beltColor = 'brown';
          else if (description.toLowerCase().includes('purple')) beltColor = 'purple';
          else if (description.toLowerCase().includes('blue')) beltColor = 'blue';
          else if (description.toLowerCase().includes('white')) beltColor = 'white';
        }
        
        // Translate based on belt color, always passing the user name
        if (beltColor && userName) {
          if (beltColor === 'white') {
            translatedDescription = t('achievements.descriptions.belt_promotion.white_belt', { name: userName });
          } else if (beltColor === 'blue') {
            translatedDescription = t('achievements.descriptions.belt_promotion.blue_belt', { name: userName });
          } else if (beltColor === 'purple') {
            translatedDescription = t('achievements.descriptions.belt_promotion.purple_belt', { name: userName });
          } else if (beltColor === 'brown') {
            translatedDescription = t('achievements.descriptions.belt_promotion.brown_belt', { name: userName });
          } else if (beltColor === 'black') {
            translatedDescription = t('achievements.descriptions.belt_promotion.black_belt', { name: userName });
          }
        }
      }
    } else if (type === 'stripe_promotion') {
      const beltColor = metadata?.belt_color || 'faixa';
      if (description.toLowerCase().includes('first stripe')) {
        translatedDescription = t('achievements.descriptions.stripe_promotion.first_stripe', { belt: beltColor });
      } else if (description.toLowerCase().includes('second stripe')) {
        translatedDescription = t('achievements.descriptions.stripe_promotion.second_stripe', { belt: beltColor });
      } else if (description.toLowerCase().includes('third stripe')) {
        translatedDescription = t('achievements.descriptions.stripe_promotion.third_stripe', { belt: beltColor });
      } else if (description.toLowerCase().includes('fourth stripe')) {
        translatedDescription = t('achievements.descriptions.stripe_promotion.fourth_stripe', { belt: beltColor });
      } else if (description.toLowerCase().includes('earned their')) {
        // Extract stripe number, belt color and user name from description like "Eduardo Silva has just earned their 2nd brown belt stripe."
        const stripeMatch = description.match(/(.+) has just earned their (\d+)(?:st|nd|rd|th) (\w+) belt stripe[.]?/i);
        
        // Get user name - try from description first, then fallback to profile
        let userName: string | null = null;
        if (stripeMatch && stripeMatch[1] && stripeMatch[1].trim().toLowerCase() !== 'you') {
          userName = stripeMatch[1].trim();
        }
        
        // If regex didn't capture name or it's "You", use profile name as fallback
        if (!userName || userName.toLowerCase() === 'you') {
          userName = getUserName();
        }
        
        if (stripeMatch && stripeMatch[2] && stripeMatch[3] && userName) {
          const stripeNumber = parseInt(stripeMatch[2]);
          const beltColor = translateBeltColor(stripeMatch[3]);
          
          let stripeKey = 'first_stripe';
          if (stripeNumber === 2) stripeKey = 'second_stripe';
          else if (stripeNumber === 3) stripeKey = 'third_stripe';
          else if (stripeNumber === 4) stripeKey = 'fourth_stripe';
          
          translatedDescription = t(`achievements.descriptions.stripe_promotion.${stripeKey}`, { 
            name: userName, 
            belt: beltColor 
          });
        }
      }
    }
    
    return {
      title: translatedTitle,
      description: translatedDescription
    };
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#160C0C] text-white font-roboto" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        <TopNavigation variant="subtle" />
        <div className="pt-16" style={{ width: '100%', maxWidth: '100vw' }}>
          {/* Loading skeleton */}
          <div className="pt-2 pb-4 box-border" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#231A1A] rounded-2xl p-4 border border-[#3A2A2A] animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#3A2A2A] rounded"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-[#3A2A2A] rounded mb-2"></div>
                      <div className="h-3 bg-[#3A2A2A] rounded w-3/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#160C0C] text-white font-roboto" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        <TopNavigation variant="subtle" />
        <div className="pt-16" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="flex items-center justify-between py-4 border-b border-[#281B1B] box-border" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <button onClick={handleBack} className="p-1 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">{t('achievements.page_title')}</h1>
          </div>
          <div className="pt-2 pb-4 box-border" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <p className="text-white/60">{t('achievements.error_loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#160C0C] text-white font-roboto" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <TopNavigation variant="subtle" />
      
      {/* Header */}
      <div className="pt-16" style={{ width: '100%', maxWidth: '100vw' }}>
        <div className="flex items-center justify-between py-4 border-b border-[#281B1B] box-border" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
          <button onClick={handleBack} className="p-1 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">{t('achievements.page_title')}</h1>
        </div>

        {/* Content */}
        <div className="pt-2 pb-4 box-border" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
          {/* Total count - Only show when there are achievements */}
          {achievements && achievements.length > 0 && (
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[14px] font-bold">{t('achievements.all_achievements')}</h2>
              <div className="text-white text-sm font-normal">{t('achievements.total')}: {achievements.length}</div>
            </div>
          )}

          {/* Achievements list */}
          {achievements && achievements.length > 0 ? (
            <div className="space-y-4">
              {/* Sort achievements by created_at DESC (most recent first) */}
              {[...achievements]
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((achievement) => {
                const { title, description } = translateAchievement(achievement);
                
                return (
                  <div key={achievement.id} className="bg-[#231A1A] rounded-2xl p-4 border border-[#3A2A2A] relative">
                    {/* Date in top right corner with label */}
                    <div className="absolute top-4 right-4 text-right">
                      <div className="text-white/60 text-[10px] mb-0.5">
                        {getDateLabel(achievement.type)}
                      </div>
                      <span className="text-white/80 text-xs font-medium">
                        {formatDate(achievement.created_at)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 pr-20">
                      {/* Achievement icon */}
                      <div className="flex-shrink-0">
                        <img
                          src={getAchievementIcon(achievement.type)}
                          alt={title}
                          className="w-10 h-10 max-h-[40px] rounded"
                        />
                      </div>

                      {/* Achievement content */}
                      <div className="flex-1 min-w-0 max-w-[calc(100%-5.5rem)]">
                        <h3 className="text-white font-semibold text-[12px] mb-1">
                          {title}
                        </h3>
                        <p className="text-white/80 text-[10px]">
                          {description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
              {/* Empty State Icon */}
              <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                <img 
                  src={AwardIcon} 
                  alt={t('achievements.page_title')} 
                  className="w-10 h-10 text-white/40"
                  style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
                />
              </div>
              
              {/* Empty State Title */}
              <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                {t('achievements.no_achievements_yet')}
              </h3>
              
              {/* Empty State Subtitle */}
              <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                {user?.id === userId ? (
                  // My profile
                  t('achievements.no_achievements_subtitle')
                ) : (
                  // Other user's profile
                  `Invite ${friendName || 'your friend'} to roll with you and help to earn achievements!`
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAchievements; 